"use client"

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Card, CardContent } from "@/components/ui/card"
import { useLanguage } from "@/lib/language-context"
import { ArrowLeft } from "lucide-react"
import Link from "next/link"

export function FAQPage() {
  const { t } = useLanguage()

  const faqCategories = [
    {
      name: t("faq.ordering"),
      items: [
        { id: "q1", question: t("faq.payment_methods_q"), answer: t("faq.payment_methods_a") },
        { id: "q2", question: t("faq.bulk_order_q"), answer: t("faq.bulk_order_a") },
      ]
    },
    {
      name: t("faq.shipping"),
      items: [
        { id: "q3", question: t("faq.shipping_time_q"), answer: t("faq.shipping_time_a") },
        { id: "q4", question: t("faq.international_q"), answer: t("faq.international_a") },
      ]
    },
    {
      name: t("faq.returns"),
      items: [
        { id: "q5", question: t("faq.return_policy_q"), answer: t("faq.return_policy_a") },
        { id: "q6", question: t("faq.return_process_q"), answer: t("faq.return_process_a") },
      ]
    }
  ]

  return (
    <main className="min-h-screen bg-background py-12">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <Link
          href="/products"
          className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary mb-8 group transition-colors"
        >
          <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />
          {t("nav.products")}
        </Link>

        <div className="mb-12 text-left">
          <h1 className="text-4xl sm:text-6xl font-black text-gray-900 tracking-tighter leading-none mb-6">
            {t("nav.faq")}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Find answers to common questions about ordering, shipping, and returns based on our latest policies.
          </p>
        </div>

        <div className="space-y-12">
          {faqCategories.map((category) => (
            <div key={category.name} className="animate-in fade-in slide-in-from-bottom-4 duration-700">
              <h2 className="text-xl font-black uppercase tracking-widest text-primary mb-6 flex items-center gap-3">
                <span className="h-1 w-8 bg-primary rounded-full"></span>
                {category.name}
              </h2>
              <Card className="border border-gray-100 shadow-sm bg-white/50 backdrop-blur-sm overflow-hidden rounded-3xl">
                <CardContent className="p-2 sm:p-6">
                  <Accordion type="single" collapsible className="w-full">
                    {category.items.map((item) => (
                      <AccordionItem key={item.id} value={item.id} className="border-b border-gray-100 last:border-0 px-4">
                        <AccordionTrigger className="hover:text-primary hover:no-underline py-6 text-left font-bold text-gray-900 leading-snug">
                          {item.question}
                        </AccordionTrigger>
                        <AccordionContent className="pb-6 text-muted-foreground leading-relaxed">
                          {item.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-20 rounded-[2.5rem] bg-gray-900 p-8 sm:p-12 text-center text-white relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl -mr-32 -mt-32"></div>
          <div className="relative z-10">
            <h2 className="text-3xl font-black mb-4 tracking-tight">Didn't find your answer?</h2>
            <p className="mb-8 text-gray-400 font-medium max-w-md mx-auto">
              Our team of industrial experts is ready to help you with any technical question or custom request.
            </p>
            <Link
              href="/contact"
              className="inline-flex h-12 items-center justify-center rounded-2xl bg-primary px-8 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary/90 hover:scale-105"
            >
              {t("nav.contact")}
            </Link>
          </div>
        </div>
      </div>
    </main>
  )
}
